import Big from 'big.js'
import { RANGOS_DURACION, type Moneda, type RangoDuracion } from './catalogos.js'

/**
 * Métricas por estudiante pedidas por el cliente. Estas funciones son la
 * referencia: la app Flutter las reimplementa en Dart y ambas se prueban con
 * los mismos vectores (`vectores/metricas.json`).
 *
 * Dinero: siempre con Big (decimal), nunca con float. Se devuelven textos con
 * 2 decimales (USD) o 4 (intermedios) para que el redondeo sea idéntico.
 */

export const VERSION_CALCULO = '1.0.0'

/** 52 semanas / 12 meses. Configurable por campaña (D-009). */
export const FACTOR_MES_DEFECTO = '4.33'

/** Duración máxima plausible de un trayecto; más que esto es error. */
export const DURACION_MAXIMA_MIN = 240

export function aMinutos(hora: string): number {
  const [h, m] = hora.split(':').map(Number) as [number, number]
  return h * 60 + m
}

/**
 * Minutos entre dos horas del mismo trayecto. Admite cruzar la medianoche
 * (por ejemplo 23:30 → 00:15 = 45 min). Devuelve null si la duración es 0 o
 * mayor a DURACION_MAXIMA_MIN (dato imposible).
 */
export function minutosEntre(salida: string, llegada: string): number | null {
  let d = aMinutos(llegada) - aMinutos(salida)
  if (d <= 0) d += 1440
  if (d === 1440 || d > DURACION_MAXIMA_MIN) return null
  return d
}

export function rangoDuracion(minutos: number): RangoDuracion {
  for (const r of RANGOS_DURACION) if (minutos >= r.min && minutos <= r.max) return r.valor
  return 'gt90'
}

/** Posición ordinal del rango (0 = menos de 15 min). */
export function indiceRango(r: RangoDuracion): number {
  return RANGOS_DURACION.findIndex((x) => x.valor === r)
}

export function aUsd(monto: string, moneda: Moneda, tasaBsPorUsd: string | null): string | null {
  if (moneda === 'USD') return new Big(monto).round(4, Big.roundHalfUp).toFixed(4)
  if (!tasaBsPorUsd || new Big(tasaBsPorUsd).lte(0)) return null
  return new Big(monto).div(tasaBsPorUsd).round(4, Big.roundHalfUp).toFixed(4)
}

export function aBs(montoUsd: string, tasaBsPorUsd: string): string {
  return new Big(montoUsd).times(tasaBsPorUsd).round(2, Big.roundHalfUp).toFixed(2)
}

export function diasMes(diasSemana: number, factorMes: string = FACTOR_MES_DEFECTO): string {
  return new Big(diasSemana).times(factorMes).round(2, Big.roundHalfUp).toFixed(2)
}

export function viajesMes(diasMesValor: string): string {
  return new Big(diasMesValor).times(2).round(2, Big.roundHalfUp).toFixed(2)
}

const RADIO_TIERRA_KM = 6371.0088

/** Distancia en línea recta (haversine). El servidor usa PostGIS geography. */
export function distanciaKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = Math.PI / 180
  const dLat = (b.lat - a.lat) * rad
  const dLng = (b.lng - a.lng) * rad
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2
  return Math.round(2 * RADIO_TIERRA_KM * Math.asin(Math.sqrt(h)) * 1000) / 1000
}

export interface EntradaMetricas {
  diasSemana: number
  horaSalidaIda: string
  horaLlegadaIda: string
  horaSalidaRegreso: string
  horaLlegadaRegreso: string
  gastoIda: { monto: string; moneda: Moneda }
  gastoRegreso: { monto: string; moneda: Moneda }
  tasa: string | null
  factorMes?: string
  distanciaRectaKm?: number | null
}

export interface Metricas {
  duracionIdaMin: number | null
  rangoIda: RangoDuracion | null
  duracionRegresoMin: number | null
  rangoRegreso: RangoDuracion | null
  diasMes: string
  viajesMes: string
  gastoIdaUsd: string | null
  gastoRegresoUsd: string | null
  gastoDiarioUsd: string | null
  gastoMensualUsd: string | null
  velocidadIdaKmh: number | null
  versionCalculo: string
}

export function calcularMetricas(e: EntradaMetricas): Metricas {
  const duracionIdaMin = minutosEntre(e.horaSalidaIda, e.horaLlegadaIda)
  const duracionRegresoMin = minutosEntre(e.horaSalidaRegreso, e.horaLlegadaRegreso)
  const dm = diasMes(e.diasSemana, e.factorMes)
  const ida = aUsd(e.gastoIda.monto, e.gastoIda.moneda, e.tasa)
  const regreso = aUsd(e.gastoRegreso.monto, e.gastoRegreso.moneda, e.tasa)
  const diario = ida !== null && regreso !== null ? new Big(ida).plus(regreso) : null
  const velocidad =
    e.distanciaRectaKm != null && duracionIdaMin
      ? Math.round((e.distanciaRectaKm / (duracionIdaMin / 60)) * 10) / 10
      : null
  return {
    duracionIdaMin,
    rangoIda: duracionIdaMin === null ? null : rangoDuracion(duracionIdaMin),
    duracionRegresoMin,
    rangoRegreso: duracionRegresoMin === null ? null : rangoDuracion(duracionRegresoMin),
    diasMes: dm,
    viajesMes: viajesMes(dm),
    gastoIdaUsd: ida === null ? null : new Big(ida).round(2, Big.roundHalfUp).toFixed(2),
    gastoRegresoUsd: regreso === null ? null : new Big(regreso).round(2, Big.roundHalfUp).toFixed(2),
    gastoDiarioUsd: diario === null ? null : diario.round(2, Big.roundHalfUp).toFixed(2),
    gastoMensualUsd: diario === null ? null : diario.times(dm).round(2, Big.roundHalfUp).toFixed(2),
    velocidadIdaKmh: velocidad,
    versionCalculo: VERSION_CALCULO,
  }
}

/** Diferencia relativa |a-b| / max(a,b). 0 si ambos son 0. */
export function diferenciaRelativa(a: string, b: string): number {
  const A = new Big(a)
  const B = new Big(b)
  const max = A.gt(B) ? A : B
  if (max.eq(0)) return 0
  return Number(A.minus(B).abs().div(max).toFixed(4))
}
